<style lang="scss">
  @import '~nouislider/distribute/nouislider.css';
  @import '~foundation-sites/scss/util/util';

  $button-width: 30px;

  .slider-container {
    .noUi-horizontal.noUi-extended {
      padding-right: 32px;
      .noUi-handle { right: -32px; }
    }

    .noUi-slider, .noUi-slider-decrement, .noUi-slider-increment {
      display: inline-block;
      vertical-align: middle;
    }

    .noUi-slider {
      height: 24px;
      width: 100%;
      background: #ECECEC;
      border-radius: 0;
      border: 0;
      box-shadow: none;

      &.with-controls {
        width: calc(100% - (35px * 2));
      }
      .noUi-handle {
        top: 0;
        height: 24px;
        box-shadow: none;
        border-radius: 0;
        background: #B2B2B2;
        opacity: 1;
        transition: 125ms opacity;
        cursor: grab;
        &.noUi-active { cursor: grabbing; }

        &:before, &:after {
          top: 6px;
          height: 10px;
        }
      }
    }

    .noUi-slider-decrement, .noUi-slider-increment {
      background: #ECECEC;
      width: $button-width;
      text-align: center;
      cursor: pointer;
      transition: 125ms background;

      &:hover { background: #B2B2B2; }
      &.noUi-slider-decrement { border-right: 3px solid white; }
      &.noUi-slider-increment { border-left: 3px solid white; }
    }

    &.disabled {
      .noUi-slider {
        cursor: not-allowed;

        .noUi-handle {
          cursor: not-allowed;
          opacity: 0;
        }
      }

      .noUi-slider-decrement, .noUi-slider-increment {
        cursor: not-allowed;
        &:hover { background: #ECECEC; }
      }
    }
  }
</style>

<template>
  <div class="slider-container" :class="{ disabled: propertyDisabled }">
    <span class="noUi-slider-decrement" v-if="controls" @click="stepSliderValue(-1)">-</span>
    <div class="noUi-slider noUi-extended" :class="{ 'with-controls': controls }"></div>
    <span class="noUi-slider-increment" v-if="controls" @click="stepSliderValue(1)">+</span>
  </div>
</template>

<script>
import _ from 'lodash';
import noUiSlider from 'nouislider/distribute/nouislider';
import { fnRun, evaluate } from '@/store/helpers';
import { formatPercent } from '@/filters';

export default {
  name: 'Slider',
  props: {
    property: {
      type: Object,
      required: true,
    },
    snap: {
      type: Boolean,
      default: false,
    },
    controls: {
      type: Boolean,
      default: true,
    },
    percent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      slider: {},
      sliderElement: {},
      triggerWatch: true,
      triggerEmit: false,
    };
  },
  computed: {
    propertyValue() {
      const value = this.fnRun(_.get(this.property, 'value'));
      return this.parseIfPercent(value);
    },
    propertyRange() {
      const range = _.get(this.property, 'range', {});

      return _.mapValues(range, (value) => {
        const returned = this.fnRun(value);

        if (_.isArray(returned)) {
          return _.map(returned, arrayValue => this.fnRun(arrayValue));
        }

        return evaluate(returned, 'number');
      });
    },
    propertyStep() {
      return this.fnRun(_.get(this.property, 'step', 1));
    },
    propertyDisabled() {
      return this.fnRun(_.get(this.property, 'disabled', false));
    },
  },
  methods: {
    parseIfPercent(value) {
      return this.percent ? formatPercent(value) : value;
    },
    getSliderValue() {
      return evaluate(this.slider.get(), 'number');
    },
    // increment should be 1 or -1
    stepSliderValue(increment) {
      if (!this.propertyDisabled) {
        const step = this.propertyStep * increment;
        const value = this.getSliderValue() + step;
        this.setSliderValue(value);
        if (step === 1 && this.getSliderValue() === 0 && value === 1) {
          this.setSliderValue(value * 2);
        }
      }
    },
    setSliderValue(value = this.propertyValue) {
      if (this.getSliderValue() !== value) {
        this.setAriaValue(value);
        this.slider.set(value);
      }
    },
    setAriaValue(value = this.propertyValue) {
      this.sliderHandle.setAttribute('aria-valuenow', value);
    },
    updateOptions(options) {
      const opts = {
        range: _.get(options, 'range', this.propertyRange),
        step: _.get(options, 'step', this.propertyStep),
      };

      this.setAriaRange(opts.range);
      this.slider.updateOptions(opts);
    },
    setAriaRange(range = this.propertyRange) {
      this.sliderHandle.setAttribute('aria-valuemin', range.min);
      this.sliderHandle.setAttribute('aria-valuemax', range.max);
    },
    setDisabled(value = this.propertyDisabled) {
      if (value) {
        this.sliderElement.setAttribute('disabled', value);
      } else {
        this.sliderElement.removeAttribute('disabled');
      }
    },
    propertyWatcher() {
      if (this.triggerWatch) {
        // We don't want to emit if externally updated
        this.triggerEmit = false;

        this.setSliderValue(this.propertyValue);
        this.setDisabled(this.propertyDisabled);
        this.updateOptions();

        _.defer(() => {
          this.triggerEmit = true;
        });
      }

      return this.triggerWatch;
    },
    generateEventListener(name) {
      return (values, handle, unencoded, tap, positions) => {
        if (this.triggerEmit) {
          // We don't want to retrigger the watch
          this.triggerWatch = false;

          this.$emit(name, {
            values,
            handle,
            unencoded,
            tap,
            positions,
          });

          // Add this to the bottom of the stack
          // ensure input elements update the slider
          _.defer(() => {
            this.triggerWatch = true;
          });
        }
      };
    },
    addEventListeners() {
      this.slider.on('start', this.start);
      this.slider.on('end', this.end);
      this.slider.on('update', this.update);
      this.slider.on('change', this.change);
    },
    removeEventListeners() {
      this.slider.off();
    },
    // Overwritten on create
    start() {},
    end() {},
    update() {},
    change() {},
  },
  watch: {
    property: {
      deep: true,
      handler() {
        this.propertyWatcher();
      },
    },
  },
  created() {
    this.start = this.generateEventListener('start');
    this.end = this.generateEventListener('end');
    this.update = this.generateEventListener('update');
    this.change = this.generateEventListener('change');
    const { state } = this.$store;
    this.fnRun = fnRun.bind(state, state);
  },
  mounted() {
    this.sliderElement = this.$el.querySelector('.noUi-slider');
    this.slider = noUiSlider.create(this.sliderElement, {
      start: this.propertyValue,
      range: this.propertyRange,
      step: this.propertyStep,
      snap: this.snap,
    });

    this.sliderHandle = this.sliderElement.querySelector('.noUi-handle');
    this.sliderHandle.setAttribute('role', 'slider');

    this.setAriaValue();
    this.setAriaRange();
    this.setDisabled();
    this.addEventListeners();
    this.triggerEmit = true;
  },
  beforeDestroy() {
    this.removeEventListeners();
  },
};
</script>
